import { z } from 'zod';
import { paginationQuerySchema, paginated } from './common.js';
export const auditListQuerySchema = paginationQuerySchema
    .extend({
    sort: z.enum(['at']).default('at'),
    entityType: z.string().optional(),
    entityId: z.string().uuid().optional(),
    action: z.string().optional(),
    /**
     * Free text over the ACTION and the ENTITY TYPE.
     *
     * Those two and no more, deliberately. They are the platform's own vocabulary,
     * always present on a row, and are what the row visibly SHOWS — so a match is
     * explainable by looking at the result. Searching the payload would match
     * text a reader cannot see, and searching the actor would need a join on every
     * audit query for a field that is null on every system action.
     *
     * `action` above stays: it is an EXACT filter, and on a compliance record the
     * ability to say "exactly this action and nothing like it" is worth keeping
     * beside a fuzzy one.
     */
    search: z.string().trim().min(1).optional(),
    actorId: z.string().uuid().optional(),
    /** Convenience filters mapping onto entityType. */
    incidentId: z.string().uuid().optional(),
    recommendationId: z.string().uuid().optional(),
    policyId: z.string().uuid().optional(),
    /** actor source: user actions, system (no actor), or provider-origin. */
    source: z.enum(['user', 'system', 'provider']).optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
})
    /*
     * **R3.** Strict, like every other list query in this file's neighbours.
     *
     * Without it the tenant audit endpoint accepted and ignored any parameter it
     * did not recognise, so a caller filtering by a misspelled field got the
     * UNFILTERED trail and no indication of it — the worst possible failure on the
     * one endpoint people consult to establish what happened. Every other list
     * refuses; this one was the exception, and there was no reason for it.
     */
    .strict();
export const auditEventSchema = z.object({
    id: z.string().uuid(),
    action: z.string(),
    entityType: z.string(),
    entityId: z.string().nullable(),
    actorId: z.string().uuid().nullable(),
    correlationId: z.string().nullable(),
    /**
     * Where the action was performed from, when it came from a request.
     *
     * NULL for anything the worker did — a scheduled job has no client address,
     * and that absence is a fact rather than a gap. Behind a proxy it is only as
     * trustworthy as that proxy: evidence for a reader, never proof.
     */
    ipAddress: z.string().nullable(),
    /**
     * The role the actor was ACTING UNDER (design Q11).
     *
     * The tenant or partner an action touched is derivable from the row later;
     * this is not. Memberships change, and a derived role — `PARENT_CHILD_ADMIN`,
     * `WL_CHILD_ADMIN` — is resolved per request and stored nowhere at all, so
     * once the request is over there is no way to reconstruct which of the three
     * authorities wrote a row.
     *
     * NULL for anything the worker did, and for every row written before the
     * column existed — neither is backfilled, because an invented role cannot be
     * told apart from a recorded one.
     */
    actorRole: z.string().nullable(),
    before: z.record(z.unknown()).nullable(),
    after: z.record(z.unknown()).nullable(),
    at: z.string(),
});
export const auditListResponseSchema = paginated(auditEventSchema);
//# sourceMappingURL=audit.js.map